import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';

const Stat = ({ label, value }) => (
  <div className="border border-sans_line bg-white p-6">
    <div className="sans-label mb-2">{label}</div>
    <div className="font-serif text-3xl text-sans_ink tabular-nums">{value}</div>
  </div>
);

const Bars = ({ data, valueKey = 'count', accent = '#6E8B6E', money = false }) => {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div className="space-y-2">
      {data.map((d) => {
        const v = d[valueKey] || 0;
        const pct = (v / max) * 100;
        return (
          <div key={d.month || d.concern} className="grid grid-cols-12 gap-3 items-center text-xs">
            <div className="col-span-3 text-sans_mute">{d.month || d.concern}</div>
            <div className="col-span-7 h-2 bg-sans_bone">
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: accent }} />
            </div>
            <div className="col-span-2 text-right text-sans_ink tabular-nums">{money ? `₹${v.toLocaleString()}` : v}</div>
          </div>
        );
      })}
    </div>
  );
};

const AdminAnalytics = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/admin/analytics').then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-sans_mute">Loading…</div>;

  return (
    <div>
      <h1 className="font-serif text-4xl text-sans_ink tracking-tight">Analytics</h1>
      <p className="text-sans_mute mt-2">Signups, engagement, revenue.</p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Users" value={d.totals.users} />
        <Stat label="Assessments" value={d.totals.assessments} />
        <Stat label="Appointments" value={d.totals.appointments} />
        <Stat label="Orders" value={d.totals.orders} />
        <Stat label="Revenue (all-time)" value={`₹${d.totals.revenue_all_time.toLocaleString()}`} />
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <Stat label="Signup → Assessment" value={`${d.conversion.signup_to_assessment}%`} />
        <Stat label="Assessment → Consultation" value={`${d.conversion.assessment_to_consultation}%`} />
        <Stat label="Signup → Consultation" value={`${d.conversion.signup_to_consultation}%`} />
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="border border-sans_line bg-white p-6">
          <div className="sans-label mb-4">Users by month</div>
          <Bars data={d.users_by_month} accent="#8A9A5B" />
        </div>
        <div className="border border-sans_line bg-white p-6">
          <div className="sans-label mb-4">Assessments by month</div>
          <Bars data={d.assessments_by_month} accent="#7A6E8C" />
        </div>
        <div className="border border-sans_line bg-white p-6">
          <div className="sans-label mb-4">Appointments by month</div>
          <Bars data={d.appointments_by_month} accent="#C28755" />
        </div>
        <div className="border border-sans_line bg-white p-6">
          <div className="sans-label mb-4">Orders by month</div>
          <Bars data={d.orders_by_month} accent="#8B5A3C" />
        </div>
        <div className="border border-sans_line bg-white p-6 md:col-span-2">
          <div className="sans-label mb-4">Revenue by month</div>
          <div className="space-y-3">
            <div className="text-xs text-sans_mute">Orders</div>
            <Bars data={d.revenue_by_month} valueKey="orders" accent="#8B5A3C" money />
            <div className="text-xs text-sans_mute mt-4">Consultations</div>
            <Bars data={d.revenue_by_month} valueKey="consultations" accent="#4E6B4A" money />
          </div>
        </div>
        <div className="border border-sans_line bg-white p-6 md:col-span-2">
          <div className="sans-label mb-4">Top health concerns (from completed assessments)</div>
          {d.top_concerns.length === 0 ? <div className="text-sm text-sans_mute">No data yet.</div> : <Bars data={d.top_concerns} accent="#B47D5C" />}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
